import type { FC } from 'react';
import { Button } from 'antd';
import { Sparkles, Search, Loader2, CheckCircle2, Brain, FileSearch, Wand2, Save, Languages } from 'lucide-react';

export type GenerationStep = 'idle' | 'context' | 'writing' | 'seo' | 'saving' | 'translating' | 'done';

const GENERATION_STEPS: { key: GenerationStep; label: string; icon: FC<{ className?: string }> }[] = [
  { key: 'context', label: 'Analyse du contexte et de la base de connaissance...', icon: Brain },
  { key: 'writing', label: "Rédaction de l'article avec l'IA...", icon: Wand2 },
  { key: 'seo', label: 'Optimisation SEO (titre, description, mots-clés)...', icon: FileSearch },
  { key: 'saving', label: 'Sauvegarde automatique du brouillon...', icon: Save },
  { key: 'translating', label: 'Traduction automatique...', icon: Languages },
  { key: 'done', label: 'Article généré et sauvegardé ✅', icon: CheckCircle2 },
];

interface BlogGenerationPipelineProps {
  generationStep: GenerationStep;
  autoTranslate: boolean;
  isGenerating: boolean;
  title: string;
  effectiveId: string | null;
  loadingSeo: boolean;
  onGenerateAI: () => void;
  onSeoSuggestions: () => void;
}

const BlogGenerationPipeline: FC<BlogGenerationPipelineProps> = ({
  generationStep,
  autoTranslate,
  isGenerating,
  title,
  effectiveId,
  loadingSeo,
  onGenerateAI,
  onSeoSuggestions,
}) => (
  <>
    {/* AI Generation pipeline */}
    {generationStep !== 'idle' && (
      <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-violet-50 p-4 dark:border-blue-800 dark:from-blue-950/50 dark:to-violet-950/50">
        <div className="space-y-3">
          {GENERATION_STEPS.map((step) => {
            const StepIcon = step.icon;
            const idx = GENERATION_STEPS.findIndex((s) => s.key === step.key);
            const currentIdx = GENERATION_STEPS.findIndex((s) => s.key === generationStep);
            const isActive = step.key === generationStep;
            const isPast = currentIdx > idx;
            const isFuture = !isActive && !isPast;
            if (step.key === 'translating' && !autoTranslate) return null;
            return (
              <div key={step.key} className={`flex items-center gap-3 transition-all duration-500 ${isFuture ? 'opacity-30' : 'opacity-100'}`}>
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-500 ${
                    isPast
                      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400'
                      : isActive
                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
                        : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
                  }`}
                >
                  {isPast ? <CheckCircle2 className="h-4 w-4" /> : isActive ? <StepIcon className="h-4 w-4 animate-pulse" /> : <StepIcon className="h-4 w-4" />}
                </div>
                <span
                  className={`text-sm transition-all duration-500 ${
                    isPast
                      ? 'text-emerald-700 dark:text-emerald-400 line-through'
                      : isActive
                        ? 'text-blue-700 dark:text-blue-300 font-medium'
                        : 'text-slate-400 dark:text-slate-500'
                  }`}
                >
                  {step.label}
                </span>
                {isActive && step.key !== 'done' && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />}
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Generate + SEO buttons */}
    <div className="flex gap-2">
      <Button
        onClick={onGenerateAI}
        disabled={isGenerating || !title.trim()}
        type={isGenerating ? 'default' : 'primary'}
        icon={isGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        className={isGenerating ? '' : '!bg-gradient-to-r !from-blue-600 !to-violet-600 !border-0 !text-white hover:!opacity-90'}
      >
        {isGenerating ? 'Génération en cours...' : '✨ Générer contenu + SEO avec IA'}
      </Button>
      <Button
        onClick={onSeoSuggestions}
        disabled={loadingSeo || !effectiveId}
        icon={loadingSeo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
      >
        {loadingSeo ? 'Analyse…' : 'Re-analyser SEO'}
      </Button>
    </div>
  </>
);

export default BlogGenerationPipeline;
